'use client';

import {
  SectionTable,
  SectionTableProps,
} from '@sbercloud/uikit-product-site-section';
import { Typography } from '@snack-uikit/typography';
import { useLayoutType } from 'src/hooks/useLayoutType';
import { Layout } from 'src/layout';
import { organizationMicroData } from 'src/scripts';
import { HeroZero } from 'src/snack/components/HeroZero';
import { SectionSocial } from 'src/snack/components/SectionSocial';
import { Benefits } from 'src/uikit/Benefits';

import { SectionMap } from '../../snack/components/SectionMap';
import s from './Contacts.module.scss';
import { getTitleTypographyProps } from './utils';

const BREADCRUMBS = [
  {
    label: 'Главная',
    href: '/',
  },
  {
    label: 'Контакты',
  },
];

const HERO = {
  title: 'Контакты',
  text: 'Мы всегда на связи и готовы помочь. Делимся полезными контактами.',
};

const BENEFITS_TECHNICAL_SUPPORT = {
  title: 'Техническая поддержка',
  grid: Benefits.grid.ThreeColumns,
  type: Benefits.type.PrimaryCard,
  withArrow: false,
  hover: Benefits.hover.OnlyTitle,
  cards: [
    {
      title: '8 800 444-24-99',
      text: 'На связи 24/7',
      slug: 'tel:88004442499',
    },
    {
      title: 'support@cloud.ru',
      text: 'Если вам удобно задать вопрос в письме',
      slug: 'mailto:support@cloud.ru',
    },
    {
      title: 'Чат в Telegram',
      text: 'Отвечают люди, а не боты',
      slug: 'https://t.me/cloudru_community',
      target: Benefits.targetLinkType.Blank,
    },
  ],
};

const BENEFITS_CONTACTS = {
  title: 'Другие полезные контакты',
  grid: Benefits.grid.ThreeColumns,
  cards: [
    {
      title: '+7 495 260-10-82',
      text: 'Офис',
      slug: 'tel:+74952601082',
    },
    {
      title: 'pr@cloud.ru',
      text: 'Пресс-служба',
      slug: 'mailto:pr@cloud.ru',
    },
    {
      title: 'quality@cloud.ru',
      text: 'Контроль качества',
      slug: 'mailto:quality@cloud.ru',
    },
    {
      title: 'hr@cloud.ru',
      text: 'HR-департамент',
      slug: 'mailto:hr@cloud.ru',
    },
    {
      title: 'Горячая линия комплаенс',
      text: 'Для вопросов корпоративной этики',
      slug: '/contacts/compliance',
      target: Benefits.targetLinkType.Blank,
    },
    {
      title: 'Консультация по вашему проекту',
      text: 'Свяжитесь с нашим экспертом, и мы подберем лучшее решение',
      slug: '/consultation ',
      target: Benefits.targetLinkType.Blank,
    },
  ],
};

const SECTION_TABLE_PROPS: Omit<SectionTableProps, 'layoutType'> = {
  title: 'Реквизиты',
  withHeader: false,
  rows: [
    [
      {
        id: 'legalName',
        content: 'Наименование юридического лица',
      },
      {
        id: 'legalValue',
        content: 'ООО «Облачные технологии»',
      },
    ],
    [
      {
        id: 'addressName',
        content: 'Почтовый адрес и офис',
      },
      {
        id: 'addressValue',
        content: '123100, Москва, 2-я Звенигородская улица, 28',
      },
    ],
    [
      {
        id: 'legalAddressName',
        content: 'Юридический адрес',
      },
      {
        id: 'legalAddressValue',
        content: 'Москва, ул. Вавилова, д. 23, стр. 1 комната № 1.207',
      },
    ],
    [
      {
        id: 'innName',
        content: 'ИНН',
      },
      {
        id: 'innValue',
        content: '7736279160',
      },
    ],
  ],
};

export function ContactsPage() {
  const layoutType = useLayoutType();

  return (
    <Layout>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(organizationMicroData),
        }}
      />
      <div className={s.root}>
        <HeroZero
          title={HERO.title}
          description={HERO.text}
          breadcrumbs={BREADCRUMBS.map((crumb) => ({
            ...crumb,
            id: crumb.label,
          }))}
        />
        <Benefits {...BENEFITS_TECHNICAL_SUPPORT} />
        <Benefits {...BENEFITS_CONTACTS} />
        <SectionMap
          layoutType={layoutType}
          id="map"
          mapUrl="https://yandex.ru/map-widget/v1/?um=constructor%3A5be353e9c91a3794de884043c2d900cb612dd6d07e97971f97ecaae1b1b81e38&amp;source=constructor"
          title="Как до нас добраться"
          contentPosition="right"
          backgroundColor="neutral-background1-level"
        >
          <div className={s.mapDirections} data-layout-type={layoutType}>
            <Typography
              tag="h3"
              family="sans"
              className={s.mapDirectionsTitle}
              {...getTitleTypographyProps(layoutType)}
            >
              Офис Cloud.ru расположен по адресу:
              <br />
              г. Москва, ул. 2-я Звенигородская 28, 123100
            </Typography>
            <div className={s.mapDirectionsDescription}>
              <Typography
                tag="h4"
                family="sans"
                className={s.mapDirectionsSubtitle}
                {...getTitleTypographyProps(layoutType)}
              >
                На метро
              </Typography>
              <Typography.SansBodyL className={s.mapDirectionsParagraph}>
                На станции «Улица 1905 года» следуйте к выходу 1. Рядом с метро
                расположен подземный переход — перейдите по нему на
                противоположную сторону Звенигородского шоссе. Двигайтесь к дому
                3Ас1 и сверните налево. Вы увидите здание с вывеской Cloud.ru.
                Обойдите его слева, чтобы попасть внутрь.
              </Typography.SansBodyL>
            </div>
            <div className={s.mapDirectionsDescription}>
              <Typography
                tag="h4"
                family="sans"
                className={s.mapDirectionsSubtitle}
                {...getTitleTypographyProps(layoutType)}
              >
                На машине
              </Typography>
              <Typography.SansBodyL className={s.mapDirectionsParagraph}>
                Вы можете воспользоваться городской парковкой или попросить
                встречающего вас сотрудника уточнить наличие мест на подземном
                паркинге и заказать для вас пропуск.
              </Typography.SansBodyL>
            </div>
            <div className={s.mapDirectionsDescription}>
              <Typography.SansBodyL className={s.mapDirectionsParagraph}>
                Не забудьте взять паспорт, он понадобится для получения пропуска
              </Typography.SansBodyL>
            </div>
          </div>
        </SectionMap>
        <SectionSocial />
        <SectionTable
          {...SECTION_TABLE_PROPS}
          layoutType={layoutType}
          className={s.detailsTable}
        />
      </div>
    </Layout>
  );
}
